import React, { useMemo } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { Tag } from 'primereact/tag';
import { FilterMatchMode } from 'primereact/api';
import { dateOnlyBodyTemplate } from '../../../../utils/dateUtils';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { TENDER_STATUS_SEVERITY, TENDER_STATUS_I18N_KEY, TENDER_STATUS_OPTIONS } from '../utils/tenderRequestStatus';

// hooks/useTenderRequestData.js'teki gruplarla birebir aynı — bkz. oradaki not. Desen,
// training-request/components/AllRequestsTable.jsx'teki "statik kolon filtresi" (filterDisplay="row")
// + statü rozeti/aksiyon ikonlarıyla aynı.
const EXACT_FILTER_FIELDS = ['vehicleModel', 'tenderRequestType', 'status'];
const TEXT_FILTER_FIELDS = ['numberOfTechnician', 'numberOfDrivers', 'trainingLanguage', 'createdName'];
const DATE_FILTER_FIELDS = ['preferTrainingDate', 'alternativeDate', 'createdDate'];

const TenderRequestTable = ({
    data,
    loading,
    onPage,
    lazyParams,
    totalCount,
    onEdit,
    activeFilters,
    onFiltersChange,
    vehicleModelOptions = [],
    tenderRequestTypeOptions = [],
    sortField,
    sortOrder,
    onSortChange,
    onShowHistory
}) => {
    const { th } = useLanguage();

    const normalizedData = useMemo(() => {
        if (Array.isArray(data)) return data;
        if (Array.isArray(data?.items)) return data.items;
        return [];
    }, [data]);

    const textMatchModeOptions = useMemo(
        () => [
            { label: th('filter.startsWith'), value: FilterMatchMode.STARTS_WITH },
            { label: th('filter.contains'), value: FilterMatchMode.CONTAINS },
            { label: th('filter.notContains'), value: FilterMatchMode.NOT_CONTAINS },
            { label: th('filter.endsWith'), value: FilterMatchMode.ENDS_WITH },
            { label: th('filter.equals'), value: FilterMatchMode.EQUALS },
            { label: th('filter.notEquals'), value: FilterMatchMode.NOT_EQUALS }
        ],
        [th]
    );

    const statusOptions = useMemo(() => TENDER_STATUS_OPTIONS.map((code) => ({ value: code, label: th(`tenderRequest.statuses.${TENDER_STATUS_I18N_KEY[code]}`) })), [th]);

    const tableFilters = useMemo(() => {
        const filters = {};
        EXACT_FILTER_FIELDS.forEach((field) => {
            filters[field] = { value: activeFilters?.[field] ?? null, matchMode: FilterMatchMode.EQUALS };
        });
        TEXT_FILTER_FIELDS.forEach((field) => {
            filters[field] = { value: activeFilters?.[field] ?? null, matchMode: activeFilters?.[`${field}MatchMode`] ?? FilterMatchMode.CONTAINS };
        });
        DATE_FILTER_FIELDS.forEach((field) => {
            filters[field] = { value: activeFilters?.[field] ?? null, matchMode: FilterMatchMode.DATE_IS };
        });
        return filters;
    }, [activeFilters]);

    const handleTableFilter = (e) => {
        const filters = e?.filters || {};
        const updated = { ...(activeFilters || {}) };

        [...EXACT_FILTER_FIELDS, ...DATE_FILTER_FIELDS].forEach((field) => {
            if (field in filters) updated[field] = filters[field]?.value ?? null;
        });
        TEXT_FILTER_FIELDS.forEach((field) => {
            if (field in filters) {
                updated[field] = filters[field]?.value ?? null;
                updated[`${field}MatchMode`] = filters[field]?.matchMode ?? FilterMatchMode.CONTAINS;
            }
        });

        onFiltersChange?.(updated);
    };

    const handleSort = (e) => {
        onSortChange?.(e.sortField, e.sortOrder);
    };

    const vehicleModelFilterTemplate = (options) => (
        <Dropdown value={options.value} options={vehicleModelOptions} optionLabel="label" optionValue="value" onChange={(e) => options.filterApplyCallback(e.value)} placeholder={th('common.search')} className="p-column-filter w-full" showClear filter style={{ width: '100%', minWidth: '10rem' }} />
    );

    const tenderRequestTypeFilterTemplate = (options) => (
        <Dropdown
            value={options.value}
            options={tenderRequestTypeOptions}
            optionLabel="label"
            optionValue="value"
            onChange={(e) => options.filterApplyCallback(e.value)}
            placeholder={th('common.search')}
            className="p-column-filter w-full"
            showClear
            style={{ width: '100%', minWidth: '10rem' }}
        />
    );

    const statusFilterTemplate = (options) => (
        <Dropdown value={options.value} options={statusOptions} optionLabel="label" optionValue="value" onChange={(e) => options.filterApplyCallback(e.value)} placeholder={th('common.search')} className="p-column-filter w-full" showClear style={{ width: '100%', minWidth: '10rem' }} />
    );

    // Tarih filtresi: warranty-claims'teki dateFilterTemplate ile aynı desen — seçilen günü
    // yerel saat diliminde 00:00:00'a normalize eder (UTC kaymasını önlemek için).
    const dateFilterTemplate = (options) => {
        const handleDateChange = (e) => {
            let normalizedDate = e.value;
            if (normalizedDate instanceof Date) {
                normalizedDate = new Date(normalizedDate.getFullYear(), normalizedDate.getMonth(), normalizedDate.getDate());
            }
            options.filterApplyCallback(normalizedDate);
        };
        return <Calendar value={options.value} onChange={handleDateChange} dateFormat="dd.mm.yy" placeholder={th('common.search')} showIcon className="p-column-filter w-full" style={{ width: '100%', minWidth: '11rem' }} inputStyle={{ width: '100%' }} />;
    };

    const locationsBodyTemplate = (rowData) => {
        if (!Array.isArray(rowData.locations) || rowData.locations.length === 0) return '';
        return rowData.locations
            .map((loc) => loc?.trainingLocationText || loc?.trainingLocation)
            .filter(Boolean)
            .join(', ');
    };

    const statusBodyTemplate = (rowData) => {
        const i18nKey = TENDER_STATUS_I18N_KEY[rowData.status];
        const label = i18nKey ? th(`tenderRequest.statuses.${i18nKey}`) : rowData.status;
        return <Tag value={label} severity={TENDER_STATUS_SEVERITY[rowData.status] || 'secondary'} />;
    };

    const actionBodyTemplate = (rowData) => (
        <div className="flex gap-2 justify-content-center flex-wrap">
            <i className="pi pi-pencil cursor-pointer text-blue-500 hover:text-blue-700" style={{ fontSize: '1.1rem' }} title={th('common.edit')} onClick={() => onEdit(rowData)} />
            <i className="pi pi-clock cursor-pointer text-500 hover:text-700" style={{ fontSize: '1.1rem' }} title={th('tenderRequest.actions.history')} onClick={() => onShowHistory?.(rowData)} />
        </div>
    );

    return (
        <DataTable
            className="p-datatable-gridlines"
            value={normalizedData}
            paginator
            rows={lazyParams.rows || 10}
            loading={loading}
            totalRecords={totalCount}
            showGridlines
            lazy
            scrollable
            scrollDirection="horizontal"
            first={lazyParams.first}
            onPage={onPage}
            filterDisplay="row"
            filters={tableFilters}
            onFilter={handleTableFilter}
            onSort={handleSort}
            sortField={sortField}
            sortOrder={sortOrder}
            dataKey="requestId"
            emptyMessage={th('tenderRequest.emptyMessage')}
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
            tableStyle={{ minWidth: '150rem' }}
            sortMode="single"
            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
        >
            <Column body={actionBodyTemplate} style={{ width: '11rem' }} align="center" frozen />
            <Column
                field="status"
                header={th('tenderRequest.columns.status')}
                style={{ width: '10rem' }}
                body={statusBodyTemplate}
                frozen
                filter
                filterField="status"
                filterElement={statusFilterTemplate}
                showFilterMenu={false}
            />
            <Column
                field="vehicleModel"
                header={th('tenderRequest.columns.vehicleModel')}
                style={{ width: '11rem' }}
                body={(row) => row.vehicleModelText || row.vehicleModel}
                filter
                filterField="vehicleModel"
                filterElement={vehicleModelFilterTemplate}
                showFilterMenu={false}
            />
            <Column
                field="tenderRequestType"
                header={th('tenderRequest.columns.tenderRequestType')}
                style={{ width: '13rem' }}
                body={(row) => row.tenderRequestTypeText || row.tenderRequestType}
                filter
                filterField="tenderRequestType"
                filterElement={tenderRequestTypeFilterTemplate}
                showFilterMenu={false}
            />
            <Column
                field="numberOfTechnician"
                header={th('tenderRequest.columns.numberOfTechnician')}
                style={{ width: '11rem' }}
                filter
                filterField="numberOfTechnician"
                filterPlaceholder={th('common.search')}
                showFilterMenu
                showClearButton={false}
                filterMatchModeOptions={textMatchModeOptions}
            />
            <Column
                field="numberOfDrivers"
                header={th('tenderRequest.columns.numberOfDrivers')}
                style={{ width: '11rem' }}
                filter
                filterField="numberOfDrivers"
                filterPlaceholder={th('common.search')}
                showFilterMenu
                showClearButton={false}
                filterMatchModeOptions={textMatchModeOptions}
            />
            <Column
                header={th('tenderRequest.columns.location')}
                style={{ width: '13rem' }}
                body={locationsBodyTemplate}
            />
            <Column
                field="preferTrainingDate"
                header={th('tenderRequest.columns.preferDate')}
                style={{ width: '11rem' }}
                body={(row) => dateOnlyBodyTemplate(row, 'preferTrainingDate')}
                sortable
                filter
                filterField="preferTrainingDate"
                filterElement={dateFilterTemplate}
                showFilterMenu={false}
            />
            <Column
                field="alternativeDate"
                header={th('tenderRequest.columns.alternativeDate')}
                style={{ width: '11rem' }}
                body={(row) => dateOnlyBodyTemplate(row, 'alternativeDate')}
                filter
                filterField="alternativeDate"
                filterElement={dateFilterTemplate}
                showFilterMenu={false}
            />
            <Column
                field="trainingLanguage"
                header={th('tenderRequest.columns.language')}
                style={{ width: '12rem' }}
                filter
                filterField="trainingLanguage"
                filterPlaceholder={th('common.search')}
                showFilterMenu
                showClearButton={false}
                filterMatchModeOptions={textMatchModeOptions}
            />
            <Column
                field="createdDate"
                header={th('tenderRequest.columns.createdDate')}
                style={{ width: '11rem' }}
                body={(row) => dateOnlyBodyTemplate(row, 'createdDate')}
                sortable
                filter
                filterField="createdDate"
                filterElement={dateFilterTemplate}
                showFilterMenu={false}
            />
            <Column
                field="createdName"
                header={th('tenderRequest.columns.createdName')}
                style={{ width: '12rem' }}
                filter
                filterField="createdName"
                filterPlaceholder={th('common.search')}
                showFilterMenu
                showClearButton={false}
                filterMatchModeOptions={textMatchModeOptions}
            />
        </DataTable>
    );
};

export default TenderRequestTable;
